import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Copy, Plus, Trash2, ExternalLink, Share2, Lock, Globe, Pencil, CalendarDays, Users, MessageCircleHeart } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import GiftersRoster from "../components/GiftersRoster";
import RegistryUpdates from "../components/RegistryUpdates";

const CATEGORIES = ["Recovery", "Comfort", "Self Care", "Entertainment", "Nutrition"];
const STATUS_COLOR = {
  available: "bg-[#E9F6EE] text-[#2D6A4F]",
  claimed: "bg-[#FFF1D6] text-[#8A5A00]",
  purchased: "bg-[#F3E6FA] text-[#6B2186]",
};

function weekOf(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d)) return null;
  const opts = { month: "short", day: "numeric" };
  return `Week of ${d.toLocaleDateString("en-US", opts)}`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [registry, setRegistry] = useState(null);
  const [items, setItems] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editRegistryOpen, setEditRegistryOpen] = useState(false);
  const [thankItem, setThankItem] = useState(null);

  const fetchRegistry = useCallback(async () => {
    try {
      const { data } = await api.get("/registry/me");
      setRegistry(data.registry);
      setItems(data.items);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load your registry.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRegistry();
  }, [fetchRegistry]);

  const shareUrl = useMemo(() => {
    if (!registry) return "";
    return `${window.location.origin}/r/${registry.slug}`;
  }, [registry]);

  const socialShareUrl = useMemo(() => {
    if (!registry) return "";
    const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `${base}/api/share/${registry.slug}`;
  }, [registry]);

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied.");
    } catch {
      toast.error("Couldn't copy. Try long-pressing to copy.");
    }
  };

  const copySocialShare = async () => {
    try {
      await navigator.clipboard.writeText(socialShareUrl);
      toast.success("Social preview link copied — paste into iMessage/WhatsApp/X.");
    } catch {
      toast.error("Couldn't copy.");
    }
  };

  const togglePrivacy = async (isPublic) => {
    try {
      const { data } = await api.put("/registry/me", { is_public: isPublic });
      setRegistry(data);
      toast.success(isPublic ? "Registry is now public." : "Registry is now private.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update privacy.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this item from your registry?")) return;
    try {
      await api.delete(`/registry/me/items/${id}`);
      setItems(items.filter((i) => i.id !== id));
      toast.success("Removed.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove item.");
    }
  };

  if (loading) {
    return (
      <div className="container-x py-16">
        <p className="font-manrope text-ink-soft animate-soft-pulse" data-testid="dashboard-loading">
          Gathering your space…
        </p>
      </div>
    );
  }

  const hasAddress = !!(user?.shipping_address?.line1);

  return (
    <div className="container-x py-12 lg:py-16" data-testid="dashboard-page">
      {/* Header */}
      <div className="card-soft p-8 lg:p-10 relative overflow-hidden">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <span className="tag-pill" data-testid="dashboard-eyebrow">Your registry</span>
            <h1 className="mt-3 font-outfit text-3xl lg:text-4xl font-medium text-ink tracking-tight" data-testid="dashboard-title">
              {registry?.title}
            </h1>
            {registry?.description && (
              <p className="mt-3 font-manrope text-ink-soft leading-relaxed">{registry.description}</p>
            )}
            <div className="mt-4 flex flex-wrap gap-4 items-center font-manrope text-sm text-ink-soft">
              <span className="inline-flex items-center gap-2" data-testid="dashboard-surgery-date">
                <CalendarDays className="h-4 w-4 text-brand" />
                {weekOf(user?.expected_surgery_date) || "No surgery date set"}
              </span>
              <span className="inline-flex items-center gap-2" data-testid="dashboard-privacy-state">
                {registry?.is_public ? <Globe className="h-4 w-4 text-brand" /> : <Lock className="h-4 w-4 text-brand" />}
                {registry?.is_public ? "Public" : "Private"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button className="btn-ghost inline-flex items-center gap-2" onClick={() => setEditRegistryOpen(true)} data-testid="edit-registry-btn">
              <Pencil className="h-4 w-4" /> Edit
            </button>
          </div>
        </div>

        {/* Share + privacy */}
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <div className="card-soft p-5 flex items-center gap-3 !shadow-none border border-[#EBE2E8]">
            <Share2 className="h-5 w-5 text-brand shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-manrope text-xs text-ink-soft">Share this link</p>
              <p className="font-manrope text-sm text-ink truncate" data-testid="dashboard-share-url">{shareUrl}</p>
              <p className="font-manrope text-xs text-ink-soft mt-1">
                Texting it? <button onClick={copySocialShare} className="text-brand font-medium hover:underline" data-testid="copy-social-share-btn">
                  Copy social-preview link
                </button> instead — shows a warm card in iMessage/WhatsApp/X.
              </p>
            </div>
            <button onClick={copyShare} className="btn-ghost text-sm inline-flex items-center gap-2" data-testid="copy-share-btn">
              <Copy className="h-4 w-4" /> Copy
            </button>
          </div>
          <div className="card-soft p-5 flex items-center justify-between !shadow-none border border-[#EBE2E8]">
            <div>
              <p className="font-outfit text-sm font-medium text-ink">Public registry</p>
              <p className="font-manrope text-xs text-ink-soft">Only people with the link can see it.</p>
            </div>
            <Switch
              checked={!!registry?.is_public}
              onCheckedChange={togglePrivacy}
              data-testid="privacy-toggle"
            />
          </div>
        </div>

        {!hasAddress && (
          <div className="mt-6 border border-[#F1D6E7] bg-[#FFF5FB] rounded-xl p-4 flex items-start gap-3" data-testid="missing-address-banner">
            <Lock className="h-4 w-4 text-brand mt-0.5" />
            <div className="flex-1">
              <p className="font-manrope text-sm text-ink">Add your shipping address before sharing.</p>
              <p className="font-manrope text-xs text-ink-soft mt-1">
                It stays private — guests never see it. Gifts just arrive.{" "}
                <Link to="/settings" className="text-brand font-medium hover:underline" data-testid="missing-address-cta">
                  Add address →
                </Link>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Items */}
      <div className="mt-10 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-outfit text-2xl font-medium text-ink">Your items</h2>
          <p className="font-manrope text-sm text-ink-soft mt-1">
            {items.length} {items.length === 1 ? "item" : "items"}
          </p>
        </div>
        <div className="flex gap-3">
          <Link to="/browse" className="btn-ghost inline-flex items-center gap-2" data-testid="browse-curated-btn">
            Browse curated
          </Link>
          <button className="btn-primary inline-flex items-center gap-2" onClick={() => setAddOpen(true)} data-testid="add-custom-item-btn">
            <Plus className="h-4 w-4" /> Add custom
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="mt-8 card-soft p-10 text-center" data-testid="dashboard-empty-state">
          <h3 className="font-outfit text-xl text-ink">Your registry is empty.</h3>
          <p className="font-manrope text-sm text-ink-soft mt-2 max-w-md mx-auto">
            Start with a few curated items — pillows, heating pads, broth — the things that actually
            help.
          </p>
          <Link to="/browse" className="btn-primary mt-6 inline-block" data-testid="empty-browse-btn">
            Browse curated items
          </Link>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((item) => (
            <div key={item.id} className="card-soft card-soft-hover p-5 flex flex-col" data-testid={`item-card-${item.id}`}>
              <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden bg-cream-surface">
                {item.image_url ? (
                  <img src={item.image_url} alt={item.product_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-ink-soft">No image</div>
                )}
                <span className={`absolute top-2 left-2 text-xs font-medium px-2 py-1 rounded-full ${STATUS_COLOR[item.claimed_status] || STATUS_COLOR.available}`} data-testid={`item-status-${item.id}`}>
                  {item.claimed_status}
                </span>
                {item.allow_group_gifting && (
                  <span className="absolute top-2 right-2 tag-pill !bg-white/95 backdrop-blur inline-flex items-center gap-1 text-xs" data-testid={`item-group-${item.id}`}>
                    <Users className="h-3 w-3" /> Group
                  </span>
                )}
              </div>
              <div className="mt-4 flex-1">
                <span className="tag-pill text-xs">{item.category}</span>
                <h3 className="mt-2 font-outfit text-base font-medium text-ink line-clamp-2" data-testid={`item-name-${item.id}`}>
                  {item.product_name}
                </h3>
                {item.price != null && (
                  <p className="mt-1 font-manrope text-sm text-ink-soft">${item.price?.toFixed(2)}</p>
                )}
                {item.claimed_by_name && (
                  <p className="mt-2 font-manrope text-xs text-ink-soft" data-testid={`item-claimed-by-${item.id}`}>
                    {item.claimed_status === "purchased" ? "Gifted by" : "Claimed by"} {item.claimed_by_name}
                  </p>
                )}
              </div>
              <div className="mt-4 flex items-center justify-between">
                <a href={item.url} target="_blank" rel="noreferrer" className="text-sm text-brand font-medium inline-flex items-center gap-1 hover:underline" data-testid={`item-view-${item.id}`}>
                  View <ExternalLink className="h-3.5 w-3.5" />
                </a>
                <div className="flex gap-2">
                  {item.claimed_by_name && (
                    <button onClick={() => setThankItem(item)} className="p-2 rounded-full hover:bg-brand-soft" title="Send thank-you" data-testid={`item-thank-${item.id}`}>
                      <MessageCircleHeart className="h-4 w-4 text-brand" />
                    </button>
                  )}
                  <button onClick={() => setEditItem(item)} className="p-2 rounded-full hover:bg-brand-soft" data-testid={`item-edit-${item.id}`}>
                    <Pencil className="h-4 w-4 text-ink-soft" />
                  </button>
                  <button onClick={() => handleDelete(item.id)} className="p-2 rounded-full hover:bg-red-50" data-testid={`item-delete-${item.id}`}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AddItemDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onAdded={(item) => setItems([item, ...items])}
      />
      <EditItemDialog
        item={editItem}
        onOpenChange={(open) => !open && setEditItem(null)}
        onSaved={(updated) => setItems(items.map((i) => (i.id === updated.id ? updated : i)))}
      />
      <EditRegistryDialog
        open={editRegistryOpen}
        onOpenChange={setEditRegistryOpen}
        registry={registry}
        onSaved={(reg) => setRegistry(reg)}
      />
      <ThankYouDialog
        item={thankItem}
        onOpenChange={(open) => !open && setThankItem(null)}
      />

      <RegistryUpdates />
      <GiftersRoster />
    </div>
  );
}

function ThankYouDialog({ item, onOpenChange }) {
  const open = !!item;
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (item) {
      setMessage(`Thank you so much for the ${item.product_name}. It really means a lot. 💕`);
    }
  }, [item]);

  if (!item) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/thank-yous", { item_id: item.id, message });
      toast.success(`Thank-you sent to ${item.claimed_by_name || "your gifter"}.`);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="thank-you-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit">Send a thank-you</DialogTitle>
          <DialogDescription>
            {item.claimed_by_name} {item.claimed_status === "purchased" ? "gifted" : "claimed"} "{item.product_name}". A quick note goes a long way.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            className="input-soft"
            required
            maxLength={1000}
            data-testid="thank-you-message"
          />
          <p className="font-manrope text-xs text-ink-soft">
            They'll see this on their Thank-yous page the next time they log in.
          </p>
          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={submitting} className="btn-primary" data-testid="thank-you-submit">
              {submitting ? "Sending…" : "Send"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddItemDialog({ open, onOpenChange, onAdded }) {
  const [form, setForm] = useState({ product_name: "", url: "", image_url: "", price: "", category: "Recovery", notes: "", allow_group_gifting: false });
  const [submitting, setSubmitting] = useState(false);

  const reset = () =>
    setForm({ product_name: "", url: "", image_url: "", price: "", category: "Recovery", notes: "", allow_group_gifting: false });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, price: form.price ? parseFloat(form.price) : null };
      const { data } = await api.post("/registry/me/items", payload);
      toast.success("Added to your registry.");
      onAdded(data);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add item.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-item-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit">Add custom item</DialogTitle>
          <DialogDescription>
            Paste any product URL. Amazon & Etsy links get auto-tagged.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="font-manrope text-sm text-ink mb-1 block">Product name</label>
            <input required value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} className="input-soft" data-testid="add-item-name" />
          </div>
          <div>
            <label className="font-manrope text-sm text-ink mb-1 block">Product URL</label>
            <input required type="url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="input-soft" placeholder="https://www.amazon.com/..." data-testid="add-item-url" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-manrope text-sm text-ink mb-1 block">Image URL (optional)</label>
              <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="input-soft" data-testid="add-item-image" />
            </div>
            <div>
              <label className="font-manrope text-sm text-ink mb-1 block">Price (optional)</label>
              <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="input-soft" data-testid="add-item-price" />
            </div>
          </div>
          <div>
            <label className="font-manrope text-sm text-ink mb-1 block">Category</label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="add-item-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`add-item-cat-${c}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="bg-cream-surface rounded-xl p-4 flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <Users className="h-4 w-4 text-brand mt-0.5" />
              <div>
                <p className="font-outfit text-sm font-medium text-ink">Allow group gifting</p>
                <p className="font-manrope text-xs text-ink-soft">
                  Friends chip in toward the item's price. Marked purchased when the goal is met. (Set a price above.)
                </p>
              </div>
            </div>
            <Switch
              checked={form.allow_group_gifting}
              onCheckedChange={(v) => setForm({ ...form, allow_group_gifting: v })}
              disabled={!form.price}
              data-testid="add-item-group-toggle"
            />
          </div>
          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={submitting} className="btn-primary" data-testid="add-item-submit">
              {submitting ? "Adding…" : "Add to registry"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditItemDialog({ item, onOpenChange, onSaved }) {
  const open = !!item;
  const [form, setForm] = useState({ product_name: "", url: "", image_url: "", price: "", category: "Recovery", notes: "", allow_group_gifting: false });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({
        product_name: item.product_name || "",
        url: item.url || "",
        image_url: item.image_url || "",
        price: item.price != null ? String(item.price) : "",
        category: item.category || "Recovery",
        notes: item.notes || "",
        allow_group_gifting: !!item.allow_group_gifting,
      });
    }
  }, [item]);

  if (!item) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, price: form.price ? parseFloat(form.price) : null };
      const { data } = await api.put(`/registry/me/items/${item.id}`, payload);
      toast.success("Updated.");
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-item-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit">Edit item</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="font-manrope text-sm text-ink mb-1 block">Product name</label>
            <input value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} className="input-soft" />
          </div>
          <div>
            <label className="font-manrope text-sm text-ink mb-1 block">Product URL</label>
            <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="input-soft" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-manrope text-sm text-ink mb-1 block">Image URL</label>
              <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="input-soft" />
            </div>
            <div>
              <label className="font-manrope text-sm text-ink mb-1 block">Price</label>
              <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="input-soft" />
            </div>
          </div>
          <div>
            <label className="font-manrope text-sm text-ink mb-1 block">Category</label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="bg-cream-surface rounded-xl p-4 flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <Users className="h-4 w-4 text-brand mt-0.5" />
              <div>
                <p className="font-outfit text-sm font-medium text-ink">Allow group gifting</p>
                <p className="font-manrope text-xs text-ink-soft">
                  Friends chip in toward the item's price.
                </p>
              </div>
            </div>
            <Switch
              checked={form.allow_group_gifting}
              onCheckedChange={(v) => setForm({ ...form, allow_group_gifting: v })}
              disabled={!form.price}
              data-testid="edit-item-group-toggle"
            />
          </div>
          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={submitting} className="btn-primary" data-testid="edit-item-submit">
              {submitting ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditRegistryDialog({ open, onOpenChange, registry, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (registry) setForm({ title: registry.title || "", description: registry.description || "" });
  }, [registry]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.put("/registry/me", form);
      toast.success("Registry updated.");
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-registry-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit">Edit registry</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="font-manrope text-sm text-ink mb-1 block">Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-soft" data-testid="edit-registry-title" />
          </div>
          <div>
            <label className="font-manrope text-sm text-ink mb-1 block">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="input-soft" data-testid="edit-registry-description" />
          </div>
          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={submitting} className="btn-primary" data-testid="edit-registry-submit">
              {submitting ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
